import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button, makeStyles, Portal, tokens, Tooltip } from "@fluentui/react-components";
import {
  Dismiss16Regular,
  QuestionCircle24Regular,
  QuestionCircleRegular,
} from "@fluentui/react-icons";

import { activeDialogSurface, isStepOnScreen } from "./dom";
import { HelpDrawer } from "./HelpDrawer";
import { HelpInspect } from "./HelpInspect";
import { HelpTour } from "./HelpTour";
import { readFloatingPref, writeFloatingPref } from "./preference";
import { helpForPath } from "./registry";
import type { HelpLanguage, HelpStep, HelpViewer } from "./types";
import { localizedContext, localizedContexts, localizedPage, stepVisibleForRole } from "./types";
import { INK, SHADOW_SOFT, uiText, Z } from "./ui";

/**
 * The entry point to the manual — two of them, in fact:
 *
 *  - a DOCKED "?" in the header's right-hand cluster. Always there. With the floating "?" off it
 *    stays clickable over an open dialog, through the portaled copy below.
 *  - a FLOATING "?" in the bottom-right corner, opt-in (see ./preference). When on, it is the one
 *    that stays clickable over a dialog, and the docked copy is not drawn.
 *
 * Mount it ONCE, in the header. `pathname`, `language` and the viewer flags arrive as props
 * rather than being read from the router, i18next and the store in here, so the whole `help/`
 * folder stays importable from a test or a Node script with no providers around it.
 */

/** A message the guide wants shown outside its own panels — wired to the app's toasts. */
export interface HelpNotice {
  severity: "info" | "warning" | "success";
  message: string;
  action?: { label: string; onClick: () => void };
}

/** The drawer's close transition — starting the tour sooner spotlights elements under the panel. */
const DRAWER_CLOSE_MS = 260;

const useStyles = makeStyles({
  /**
   * The portal's own mount node. Fluent gives every portal container `z-index: 1000000` and a
   * stacking context, so the z-index on the content inside counts only within it — a dialog
   * opened AFTER the floating "?" mounted landed on top of it and swallowed its clicks.
   */
  portalLayer: { zIndex: Z.portal },
  // Sized like the header's other icon buttons (large, shrunk to the icon) so the row lines up.
  docked: { color: "inherit", minWidth: "fit-content" },
  dockedActive: { color: tokens.colorBrandForeground1, minWidth: "fit-content" },
  hidden: { visibility: "hidden" },
  elevated: {
    position: "fixed",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.35)",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1 },
  },
  floatingWrap: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    // The dismiss badge is a hover affordance — always-on it would invite a mis-click.
    "&:hover .help-fab-dismiss": { opacity: 1, pointerEvents: "auto" },
    "&:focus-within .help-fab-dismiss": { opacity: 1, pointerEvents: "auto" },
  },
  floating: { boxShadow: SHADOW_SOFT },
  dismiss: {
    position: "absolute",
    top: "-6px",
    right: "-6px",
    width: "22px",
    height: "22px",
    minWidth: "22px",
    padding: 0,
    borderRadius: "50%",
    // Dark on purpose: a white badge disappeared into the page behind it.
    backgroundColor: INK,
    color: "#ffffff",
    border: `2px solid ${tokens.colorNeutralBackground1}`,
    opacity: 0,
    pointerEvents: "none",
    transitionProperty: "opacity",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: INK, color: "#ffffff" },
  },
});

/**
 * Whether one of the app's modal dialogs is on screen. Watched rather than measured once, because
 * the docked "?" has to hand over to its elevated copy the moment a dialog opens. State is set
 * only on change: the observer hears every re-render of the page.
 */
const useDialogOpen = (): boolean => {
  const [open, setOpen] = useState(false);
  const last = useRef(false);

  useEffect(() => {
    const read = (): void => {
      const next = activeDialogSurface() !== null;
      if (next === last.current) return;
      last.current = next;
      setOpen(next);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return open;
};

interface HelpLauncherProps {
  pathname: string;
  /** The guide language — the shell maps the app's i18next language through `helpLanguageFor`. */
  language: HelpLanguage;
  viewer: HelpViewer;
  /** The app's toast system. */
  notify: (notice: HelpNotice) => void;
  /** The colour of the header's other icons, so the docked "?" matches them. */
  iconColor?: string;
}

/** Same glyph size as the header's other icon buttons. */
const DOCKED_ICON_SIZE = 25;

export const HelpLauncher = ({
  pathname,
  language,
  viewer,
  notify,
  iconColor,
}: HelpLauncherProps) => {
  const styles = useStyles();
  const text = uiText(language);
  // Keyed to the route rather than a boolean: navigating away closes the panel by definition.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [tour, setTour] = useState<{
    steps: HelpStep[];
    scope: HTMLElement | null;
    startIndex: number;
  } | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [floating, setFloating] = useState(readFloatingPref);
  const dialogOpen = useDialogOpen();
  /**
   * Whether the docked "?" hands over to its copy above the dialog. Not while the floating "?"
   * is on: that one already stays clickable over a dialog, and a second "?" drawn over the
   * dialog's own header (a side panel's title, say) only gets in the way.
   */
  const elevate = dialogOpen && !floating;
  /** Where the docked button sits, so the copy that outranks a dialog can stand on it. */
  const dockRef = useRef<HTMLButtonElement>(null);
  const [dockRect, setDockRect] = useState<DOMRect | null>(null);

  /**
   * The header is its own stacking context (it has a z-index of its own), so the docked "?"
   * cannot be lifted over a dialog's backdrop from inside it. While a dialog is up the button is
   * re-rendered as a portaled copy pinned to the same rectangle.
   */
  useEffect(() => {
    if (!elevate) {
      setDockRect(null);
      return;
    }
    const measure = (): void => {
      const el = dockRef.current;
      if (el) setDockRect(el.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [elevate]);

  // Stable, because the tour re-runs its measuring effect (and its scroll) when this changes.
  const closeTour = useCallback(() => setTour(null), []);

  const help = helpForPath(pathname);

  const roleSteps = useMemo(
    () => (help ? help.steps.filter((step) => stepVisibleForRole(step, viewer)) : []),
    [help, viewer]
  );

  // An unregistered route has no guide and therefore no button.
  if (!help) return null;

  const open = openFor === pathname;
  const pageTitle = localizedPage(help, language).title;
  const toggle = (): void => setOpenFor(open ? null : pathname);

  const chooseFloating = (on: boolean): void => {
    setFloating(on);
    writeFloatingPref(on);
    if (!on) {
      notify({
        severity: "success",
        message: text.floatingHidden,
        action: { label: text.undo, onClick: () => chooseFloating(true) },
      });
    }
  };

  // Hidden while the panel, a tour or the inspector is up.
  const showFloating = floating && !open && tour === null && !inspecting;

  /** Open the tour — from the beginning, or at the step picked in the panel or the inspector. */
  const startTour = (from?: HelpStep): void => {
    setOpenFor(null);
    setInspecting(false);
    // Measured after the drawer has closed: the panel would otherwise cover the elements being
    // spotlighted.
    window.setTimeout(() => {
      // A tour started over an open dialog stays INSIDE it — the page behind the backdrop
      // cannot be clicked, and the dialog is usually what the guide was opened for.
      const surface = activeDialogSurface();
      // Role-visible AND actually rendered within that surface: the tour never spotlights a hole.
      const visible = roleSteps.filter((step) => isStepOnScreen(step, surface ?? document));
      if (visible.length === 0) {
        notify({
          severity: "info",
          message: surface ? text.noticeDialogEmpty : text.noticeNothing,
        });
        return;
      }
      const startIndex = from ? visible.indexOf(from) : 0;
      if (from && startIndex < 0) {
        // The drawer lists the WHOLE guide, including controls behind a UI action — say which,
        // and how to reach it, rather than silently starting the tour somewhere else.
        const hint = from.context ? localizedContext(help, from.context, language) : undefined;
        notify({
          severity: "info",
          message: hint ? text.noticeNotYet(from.title, hint) : text.noticeNotNow(from.title),
        });
        return;
      }
      setTour({ steps: visible, scope: surface, startIndex: Math.max(startIndex, 0) });
    }, DRAWER_CLOSE_MS);
  };

  /** Hand over to click-to-explain. Same delay as the tour, and for the same reason. */
  const startInspect = (): void => {
    setOpenFor(null);
    setTour(null);
    window.setTimeout(() => setInspecting(true), DRAWER_CLOSE_MS);
  };

  const dockedButton = (
    <Button
      ref={dockRef}
      size="large"
      appearance="transparent"
      icon={<QuestionCircleRegular fontSize={DOCKED_ICON_SIZE} />}
      aria-label={text.launcherAria(pageTitle)}
      data-help="app-help-launcher"
      onClick={toggle}
      className={elevate ? styles.hidden : open ? styles.dockedActive : styles.docked}
      // The open state keeps the brand colour from its class.
      style={open ? undefined : { color: iconColor }}
      // While the elevated copy stands on top of it this one is inert, and leaving it in the
      // accessibility tree would announce the same button twice.
      {...(elevate ? { "aria-hidden": true, tabIndex: -1 } : {})}
    />
  );

  return (
    <>
      {elevate ? (
        dockedButton
      ) : (
        <Tooltip content={text.launcherTooltip(pageTitle)} relationship="description">
          {dockedButton}
        </Tooltip>
      )}

      {elevate && (
        <Portal mountNode={{ className: styles.portalLayer }}>
          <Tooltip content={text.launcherTooltip(pageTitle)} relationship="description">
            <Button
              size="large"
              appearance="transparent"
              icon={<QuestionCircleRegular fontSize={DOCKED_ICON_SIZE} />}
              aria-label={text.launcherAria(pageTitle)}
              onClick={toggle}
              className={styles.elevated}
              style={{
                ...(iconColor && !open ? { color: iconColor } : {}),
                ...(dockRect
                  ? {
                      top: dockRect.top,
                      left: dockRect.left,
                      width: dockRect.width,
                      height: dockRect.height,
                    }
                  : // Only if the measurement failed: roughly where the header keeps it.
                    { top: 12, right: 120 }),
                zIndex: Z.launcherCopy,
              }}
            />
          </Tooltip>
        </Portal>
      )}

      {showFloating && (
        <Portal mountNode={{ className: styles.portalLayer }}>
          <div className={styles.floatingWrap} style={{ zIndex: Z.floating }}>
            <Tooltip
              content={text.launcherTooltip(pageTitle)}
              relationship="description"
              positioning="before"
            >
              <Button
                appearance="primary"
                shape="circular"
                size="large"
                icon={<QuestionCircle24Regular />}
                aria-label={text.floatingAria}
                onClick={toggle}
                className={styles.floating}
              />
            </Tooltip>
            <Tooltip content={text.floatingHide} relationship="description" positioning="above">
              <Button
                className={`help-fab-dismiss ${styles.dismiss}`}
                size="small"
                icon={<Dismiss16Regular />}
                aria-label={text.floatingHideAria}
                onClick={() => chooseFloating(false)}
              />
            </Tooltip>
          </div>
        </Portal>
      )}

      <HelpDrawer
        open={open}
        help={help}
        steps={roleSteps}
        floating={floating}
        onFloatingChange={chooseFloating}
        language={language}
        onClose={() => setOpenFor(null)}
        onStartTour={startTour}
        onInspect={startInspect}
        notify={notify}
      />

      {tour && (
        <HelpTour
          steps={tour.steps}
          scope={tour.scope}
          startIndex={tour.startIndex}
          sections={help.sections}
          language={language}
          onInspect={startInspect}
          onClose={closeTour}
        />
      )}

      {inspecting && (
        <HelpInspect
          steps={roleSteps}
          language={language}
          contexts={localizedContexts(help, language)}
          onTourFrom={startTour}
          onExit={() => setInspecting(false)}
        />
      )}
    </>
  );
};
